import { useCallback, useEffect, useState } from "react";
import { Eye, EyeOff, MapPinned, Pencil, Plus, UserCog } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { EmptyState, Field, PageHeader, StatusPill, UsernameInput } from "@/components/common";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { emailLocal, ROLE_LABELS, toEmail } from "@/lib/format";

const EMPTY = { name: "", email: "", password: "", role: "petugas", resort_id: "none" };

export const ResortSelect = ({ value, onChange, resorts, allowNone = true, testId = "resort-select" }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="h-11" data-testid={testId}><SelectValue placeholder="Pilih resort" /></SelectTrigger>
    <SelectContent>
      {allowNone && <SelectItem value="none">Belum ditentukan</SelectItem>}
      {resorts.map((r) => <SelectItem key={r.id} value={r.id} data-testid={`${testId}-${r.id}`}>{r.name}</SelectItem>)}
    </SelectContent>
  </Select>
);

const StaffForm = ({ open, onOpenChange, initial, onSaved, resorts }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF(initial ? { name: initial.name, email: emailLocal(initial.email), role: initial.role, password: "", resort_id: initial.resort_id || "none" } : EMPTY); }, [open, initial]);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...f, email: toEmail(f.email), resort_id: null };
      if (initial) await api.put(`/staff/${initial.id}`, { ...payload, password: f.password || null });
      else await api.post("/staff", payload);
      toast.success(initial ? "Data petugas diperbarui" : "Petugas ditambahkan");
      onOpenChange(false); onSaved();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[92vh] overflow-y-auto" data-testid="staff-form-dialog">
        <DialogHeader><DialogTitle className="font-heading">{initial ? "Ubah Data Petugas" : "Tambah Petugas"}</DialogTitle><DialogDescription>Akun login untuk staf kantor cabang.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Nama Lengkap"><Input required className="h-11" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="staff-name-input" /></Field>
          <Field label="Username Login" hint={`Email login otomatis: ${f.email || "nama_akun"}@kspbam.com`}>
            <UsernameInput value={f.email} onChange={(v) => setF({ ...f, email: v })} required testId="staff-email-input" />
          </Field>
          <Field label="Peran">
            <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
              <SelectTrigger className="h-11" data-testid="staff-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["kepala_bagian", "kasir", "petugas"].map((r) => <SelectItem key={r} value={r} data-testid={`staff-role-option-${r}`}>{ROLE_LABELS[r]}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          {f.role === "petugas" && <p className="text-xs text-muted-foreground">Resort petugas lapangan diatur di menu Resort.</p>}
          <Field label={initial ? "Password Baru (opsional)" : "Password"} hint="Minimal 8 karakter"><Input type="password" required={!initial} minLength={8} className="h-11" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="staff-password-input" /></Field>
          <DialogFooter><Button type="submit" disabled={busy} className="rounded-full h-11 px-6" data-testid="staff-form-submit">{busy ? "Menyimpan..." : "Simpan"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const StaffCard = ({ s, onEdit, onToggle, onHide, hiddenView }) => (
  <div className="panel !p-5 min-w-0" data-testid={`staff-card-${s.id}`}>
    <div className="flex items-start justify-between gap-3">
      <div className="flex items-center gap-3 min-w-0">
        <span className="h-11 w-11 rounded-full bg-primary/12 text-primary grid place-items-center font-heading font-bold shrink-0">{s.name[0]}</span>
        <div className="min-w-0"><p className="font-semibold truncate">{s.name}</p><p className="text-xs text-muted-foreground truncate">{s.email}</p></div>
      </div>
      <StatusPill active={s.active} testId={`staff-status-${s.id}`} />
    </div>
    <div className="mt-3 flex flex-wrap gap-1.5 text-xs">
      <span className="font-semibold rounded-full bg-muted px-2.5 py-1">{ROLE_LABELS[s.role]}</span>
      {s.role === "petugas" && <span className="rounded-full bg-muted px-2.5 py-1 flex items-center gap-1"><MapPinned className="h-3 w-3" />{s.resort_name || "Tanpa resort"}</span>}
    </div>
    <div className="mt-4 pt-4 border-t flex flex-nowrap items-center gap-1.5 overflow-x-auto">
      {hiddenView ? (
        <Button size="sm" className="h-9 rounded-full px-3 text-xs" onClick={() => onHide(s)} data-testid={`staff-unhide-${s.id}`}><Eye className="h-3.5 w-3.5 mr-1" />Tampilkan</Button>
      ) : <>
        <Button variant="outline" size="sm" className="h-9 rounded-full px-3 text-xs shrink-0" onClick={() => onEdit(s)} data-testid={`staff-edit-${s.id}`}><Pencil className="h-3.5 w-3.5 mr-1" />Ubah</Button>
        <Button size="sm" className={`h-9 rounded-full px-3 text-xs text-white shrink-0 ${s.active ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"}`} onClick={() => onToggle(s, !s.active)} data-testid={`staff-toggle-${s.id}`}>{s.active ? "Aktif" : "Tidak Aktif"}</Button>
        <Button variant="ghost" size="sm" className="h-9 rounded-full px-3 text-xs ml-auto shrink-0" onClick={() => onHide(s)} data-testid={`staff-hide-${s.id}`}><EyeOff className="h-3.5 w-3.5 mr-1" />Sembunyikan</Button>
      </>}
    </div>
  </div>
);

export default function StaffPage({ hiddenView = false }) {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [hide, setHide] = useState(null);
  const load = useCallback(() => { api.get("/staff", { params: { hidden: hiddenView } }).then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e))); }, [hiddenView]);
  useEffect(() => { load(); }, [load]);
  const doHide = async () => {
    try { await api.patch(`/staff/${hide.id}/hidden`, { hidden: !hiddenView }); toast.success(hiddenView ? `${hide.name} ditampilkan kembali` : `${hide.name} disembunyikan`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const toggle = async (s, active) => {
    try { await api.patch(`/staff/${s.id}/status`, { active }); load(); toast.success(`${s.name} ${active ? "diaktifkan" : "dinonaktifkan"}`); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <PageHeader title={hiddenView ? "Akun Tersembunyi" : "Kelola Petugas"}
        action={!hiddenView && <Button className="rounded-full h-11 px-6" onClick={() => { setEditing(null); setOpen(true); }} data-testid="add-staff-button"><Plus className="h-4 w-4 mr-2" />Tambah Petugas</Button>} />
      {items === null ? <p className="text-muted-foreground">Memuat...</p> : items.length === 0 ? (
        <EmptyState icon={UserCog} title={hiddenView ? "Tidak ada akun tersembunyi" : "Belum ada petugas"} testId="staff-empty" />
      ) : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map((s) => <StaffCard key={s.id} s={s} hiddenView={hiddenView} onHide={setHide} onEdit={(x) => { setEditing(x); setOpen(true); }} onToggle={toggle} />)}
        </div>
      )}
      <StaffForm open={open} onOpenChange={setOpen} initial={editing} onSaved={load} />
      <ConfirmDialog open={Boolean(hide)} onOpenChange={(o) => !o && setHide(null)} onConfirm={doHide}
        title={hiddenView ? "Tampilkan Akun" : "Sembunyikan Akun"} confirmLabel={hiddenView ? "Ya, tampilkan" : "Ya, sembunyikan"}
        description={hide && (hiddenView ? <>Tampilkan kembali akun <b>{hide.name}</b>?</> : <>Sembunyikan akun <b>{hide.name}</b>? Akun tidak dihapus dan bisa ditampilkan lagi dari menu Akun Tersembunyi.</>)} />
    </div>
  );
}
